import React from "react";
import i18n from "../i18n/i18n";
import {DateUtils} from "./dateUtils";
import {
    DamageModelWithDirection,
    DamageModelWithoutDirection,
    DamageType,
    DamageTypeFirstLetter,
    EquipmentType,
    SingleTrailerConditionReportModel,
    TrailerConditionReportType
} from "../transportation/models/tcr";
import {ITcrStore} from "../transportation/stores/tcrStore";
import {Utils} from "./utils";
import {Direction} from "../transportation/models/transport";

export default class TcrUtils {
    public static renderDamageUpdateTime(value: string): JSX.Element {
        const dateTmp = DateUtils.getFormattedTimeWithDate(value).split("-");

        return <><b>{dateTmp[0]}</b> {dateTmp[1]}</>;
    }

    public static renderDamageType(value: string): JSX.Element {
        const damageTypeFirstLetter = DamageTypeFirstLetter[value as DamageType];

        if (damageTypeFirstLetter && value) {
            return <>
                <span>({damageTypeFirstLetter}) </span>
                <span>{i18n.t(`transportation.order.tcr.sections.trailerCondition.damageType.${value}`)}</span>
            </>;
        } else {
            return <></>;
        }
    }

    public static renderEquipment(row: any): JSX.Element {
        const damageLocation = row.damageLocation;
        const equipmentType = row.equipmentType;
        const translatedDamageLocation = i18n.t(`transportation.order.tcr.sections.trailerCondition.damageLocation.${damageLocation}`);

        if (!equipmentType) {
            return <></>;
        }

        return <>
            <span>{i18n.t(`transportation.order.tcr.equipmentType.${equipmentType}`)}</span>
            {damageLocation && (
                <span> - {translatedDamageLocation}</span>
            )}
        </>;
    }

    public static renderTcrCode(row: any): JSX.Element {
        const equipmentType = row.equipmentType;
        const damageType = row.damageType;

        if (!equipmentType || !damageType || !TcrUtils.getEquipmentNumber(equipmentType)) {
            return <></>;
        }

        const damageTypeFirstLetter = DamageTypeFirstLetter[damageType as DamageType];
        const code = `${damageTypeFirstLetter}${TcrUtils.getEquipmentNumber(equipmentType)}`;

        return <span>{code}</span>;
    }

    public static getEquipmentNumber(equipmentType: EquipmentType) {
        switch (equipmentType) {
            case EquipmentType.TARPAULIN:
                return "1";
            case EquipmentType.SIDE_BOARD:
                return "2";
            case EquipmentType.LEG:
                return "4";
            case EquipmentType.WHEEL:
                return "5";
            case EquipmentType.LIGHT:
                return "8";
            case EquipmentType.AGGREGATE:
                return "11";
            case EquipmentType.BICYCLE_PROTECTION:
                return "12";
            default:
                return "";
        }
    }

    public static prepareTcrData(tcrStore: ITcrStore): SingleTrailerConditionReportModel {
        if (tcrStore.direction === Direction.IN) {
            return {
                actualTemp1: Utils.convertToNumberOrNull(Utils.changeCommaToDot(tcrStore.entryActualTemp1.value)),
                actualTemp2: Utils.convertToNumberOrNull(Utils.changeCommaToDot(tcrStore.entryActualTemp2.value)),
                spareWheelsNo: Utils.convertToNumberOrNull(tcrStore.entrySpareWheelsNo.value),
                wedgesNo: Utils.convertToNumberOrNull(tcrStore.entryWedgesNo.value),
                plumb: tcrStore.entryPlumb.value,
                winch: tcrStore.entryWinch.value,
                damages: this.getDamagesForDirection(Direction.IN, tcrStore.damages),
                reportType: TrailerConditionReportType.PICK_UP,
                orderId: tcrStore.orderId
            };
        } else if (tcrStore.direction === Direction.OUT) {
            return {
                actualTemp1: Utils.convertToNumberOrNull(Utils.changeCommaToDot(tcrStore.exitActualTemp1.value)),
                actualTemp2: Utils.convertToNumberOrNull(Utils.changeCommaToDot(tcrStore.exitActualTemp2.value)),
                spareWheelsNo: Utils.convertToNumberOrNull(tcrStore.exitSpareWheelsNo.value),
                wedgesNo: Utils.convertToNumberOrNull(tcrStore.exitWedgesNo.value),
                plumb: tcrStore.exitPlumb.value,
                winch: tcrStore.exitWinch.value,
                damages: this.getDamagesForDirection(Direction.OUT, tcrStore.damages),
                reportType: TrailerConditionReportType.GIVE_BACK,
                orderId: tcrStore.orderId
            };
        } else {
            return {
                actualTemp1: null,
                actualTemp2: null,
                spareWheelsNo: null,
                wedgesNo: null,
                plumb: false,
                winch: false,
                damages: [],
                reportType: null,
                orderId: tcrStore.orderId
            };
        }
    }

    public static getDamagesForDirection(direction: Direction, allDamages: DamageModelWithDirection[]): DamageModelWithoutDirection[] {
        const damages: DamageModelWithoutDirection[] = [];

        allDamages.forEach((damage) => {
            if (damage.direction === direction) {
                damages.push({ // without direction
                    damageUpdateTime: damage.damageUpdateTime,
                    sideType: damage.sideType,
                    equipmentType: damage.equipmentType,
                    damageType: damage.damageType,
                    damageLocation: damage.damageLocation
                });
            }
        });

        return damages;
    }

    public static setDirectionInDamages(direction: Direction, damagesForApi?: DamageModelWithoutDirection[]): DamageModelWithDirection[] {
        if (!damagesForApi) {
            return [];
        }

        const damages: DamageModelWithDirection[] = [];
        damagesForApi.map((damage) => {
            damages.push({
                ...damage,
                direction
            });
        });

        return damages;
    }

    public static getDirection(trailerConditionReportType: TrailerConditionReportType | null): Direction | null {
        switch (trailerConditionReportType) {
            case TrailerConditionReportType.PICK_UP:
                return Direction.IN;
            case TrailerConditionReportType.GIVE_BACK:
                return Direction.OUT;
            default:
                return null;
        }
    }

    public static isEquipmentDamaged(damages: DamageModelWithDirection[], damage: DamageModelWithDirection): boolean {
        let isDamaged = false;

        damages.map((damageFromList: DamageModelWithDirection) => {
            const theSameSideType = damageFromList.sideType === damage.sideType;
            const theSameEquipmentType = damageFromList.equipmentType === damage.equipmentType;
            const theSameDamageType = damageFromList.damageType === damage.damageType;
            const theSameDamageLocation = (!damage.damageLocation && !damageFromList.damageLocation) || damageFromList.damageLocation === damage.damageLocation;
            const theSameDirection = damageFromList.direction === damage.direction;

            if (theSameSideType && theSameEquipmentType && theSameDamageType && theSameDamageLocation && theSameDirection) {
                isDamaged = true;
            }
        });

        return isDamaged;
    }

    public static damageIndex(selectedDamages: DamageModelWithDirection[], damage: DamageModelWithDirection): number | undefined {
        let index = undefined;

        selectedDamages.map((damageFromList: DamageModelWithDirection, id: number) => {
            const theSameSideType = damageFromList.sideType === damage.sideType;
            const theSameEquipmentType = damageFromList.equipmentType === damage.equipmentType;
            const theSameDamageType = damageFromList.damageType === damage.damageType;
            const theSameDamageLocation = (!damage.damageLocation && !damageFromList.damageLocation) || damageFromList.damageLocation === damage.damageLocation;
            const theSameDirection = damageFromList.direction === damage.direction;

            if (theSameSideType && theSameEquipmentType && theSameDamageType && theSameDamageLocation && theSameDirection) {
                index = id;
            }
        });

        return index;
    }

    public static allDamagesInTheSameLocation(damages: DamageModelWithDirection[], damage: DamageModelWithDirection): DamageModelWithDirection[] {
        const fewDamagesInTheSameLocation: DamageModelWithDirection[] = [];

        damages.map((damageFromList: DamageModelWithDirection) => {
            const theSameSideType = damageFromList.sideType === damage.sideType;
            const theSameEquipmentType = damageFromList.equipmentType === damage.equipmentType;
            const theSameDamageLocation = (!damage.damageLocation && !damageFromList.damageLocation) || damageFromList.damageLocation === damage.damageLocation;

            if (theSameSideType && theSameEquipmentType && theSameDamageLocation) {
                fewDamagesInTheSameLocation.push(damageFromList);
            }
        });

        return fewDamagesInTheSameLocation;
    }

    public static renderEquipmentForFile(damageLocation: string | undefined, equipmentType: string) {
        const translatedDamageLocation = damageLocation ?
            ` - ${i18n.t(`transportation.order.tcr.sections.trailerCondition.damageLocation.${damageLocation}`)}` : "";

        if (!equipmentType) {
            return "";
        }

        return `${i18n.t(`transportation.order.tcr.equipmentType.${equipmentType}`)}${translatedDamageLocation}`;
    }

    public static renderDamageUpdateTimeForFile(value: string) {
        const dateTmp = DateUtils.getFormattedTimeWithDate(value).split("-");

        return `${dateTmp[1]} ${dateTmp[0]}`;
    }

    public static renderDamageTypeForFile(value: string) {
        const damageTypeFirstLetter = DamageTypeFirstLetter[value as DamageType];

        if (damageTypeFirstLetter && value) {
            return `${i18n.t(`transportation.order.tcr.sections.trailerCondition.damageType.${value}`)} (${damageTypeFirstLetter})`;
        } else {
            return "";
        }
    }

    public static renderSideTypeForFile(value: string) {
        if (!value) return "";

        return i18n.t(`transportation.order.tcr.sections.trailerCondition.sideType.${value}`);
    }
}